import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  BsChevronDown,
  BsChevronRight,
  BsDownload,
  BsEye,
  BsFileEarmarkPlus,
  BsSearch,
} from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import memoApi from "../services/memoApi";

// TEMP: all statuses eligible
const ELIGIBLE_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
  "SENT_BACK",
  "PO_SELECTED",
];

const formatDateForInput = (date = new Date()) => {
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
};

const InterOfficeMemo = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [requestRows, setRequestRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoadingByRequest, setActionLoadingByRequest] = useState({});
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const fetchRows = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequests();
      const requests = response?.data || [];

      const eligibleRequests = requests.filter((req) =>
        ELIGIBLE_STATUSES.includes(req.status),
      );

      const memoResults = await Promise.allSettled(
        eligibleRequests.map((req) => memoApi.getMemoByRequestId(req._id)),
      );

      const merged = eligibleRequests.map((req, idx) => {
        const memoResult = memoResults[idx];
        const memo =
          memoResult?.status === "fulfilled"
            ? memoResult.value?.data || null
            : null;

        return {
          request: req,
          memo,
        };
      });

      setRequestRows(merged);
    } catch (error) {
      toast({
        title: "Failed to load memo report",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const setRowActionLoading = (requestId, isLoading) => {
    setActionLoadingByRequest((prev) => ({
      ...prev,
      [requestId]: isLoading,
    }));
  };

  const handleGenerateMemo = async (row) => {
    const request = row.request;
    const requestId = request._id;

    // Keep generation simple and deterministic for quick ops from report list.
    const payload = {
      memoNumber: `IOM-${String(requestId).slice(-6).toUpperCase()}`,
      memoDate: formatDateForInput(),
      department: request.department || "General",
      subject: request.title || "Inter Office Memo",
    };

    try {
      setRowActionLoading(requestId, true);
      await memoApi.generateMemo(requestId, payload);
      toast({
        title: row.memo ? "IOM re-generated" : "IOM generated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await fetchRows();
    } catch (error) {
      toast({
        title: "Failed to generate IOM",
        description:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setRowActionLoading(requestId, false);
    }
  };

  const toggleVersionHistory = useCallback(
    async (requestId) => {
      if (expandedRequest === requestId) {
        setExpandedRequest(null);
        return;
      }
      try {
        setVersionsLoading(true);
        setExpandedRequest(requestId);
        const response = await memoApi.getMemoVersions(requestId);
        setVersionHistory(response?.data || []);
      } catch {
        setVersionHistory([]);
      } finally {
        setVersionsLoading(false);
      }
    },
    [expandedRequest],
  );

  const handleDownloadMemo = async (memoId) => {
    try {
      const response = await memoApi.downloadMemoPdf(memoId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition =
        response.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^\"]+)"?/i);
      const fileName = fileNameMatch?.[1] || `IOM-${memoId}.pdf`;

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Failed to download IOM",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePreviewMemo = async (memoId) => {
    try {
      setPreviewLoading(true);
      const response = await memoApi.downloadMemoPdf(memoId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke after a delay to let the new tab load
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch (error) {
      toast({
        title: "Failed to preview IOM",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    return requestRows.filter(({ request, memo }) => {
      const matchesSearch = !search
        ? true
        : [
            request.title,
            request.requestNumber,
            request.department,
            request._id,
            memo?.memoNumber,
          ]
            .filter(Boolean)
            .some((v) =>
              String(v).toLowerCase().includes(search.toLowerCase()),
            );

      const memoState = memo ? "generated" : "pending";
      const matchesStatus =
        statusFilter === "all" ? true : memoState === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [requestRows, search, statusFilter]);

  const stats = useMemo(() => {
    const totalEligible = requestRows.length;
    const generated = requestRows.filter((r) => Boolean(r.memo)).length;
    const pending = totalEligible - generated;
    return { totalEligible, generated, pending };
  }, [requestRows]);

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="120px" mb={4} />
        <Skeleton height="420px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size={{ base: "md", md: "lg" }}>
          Inter Office Memo Report
        </Heading>
        <Button
          variant="outline"
          onClick={fetchRows}
          size={{ base: "sm", md: "md" }}
        >
          Refresh
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Eligible Requests</StatLabel>
            <StatNumber>{stats.totalEligible}</StatNumber>
          </Stat>
        </Box>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Generated IOMs</StatLabel>
            <StatNumber color="green.500">{stats.generated}</StatNumber>
          </Stat>
        </Box>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Pending IOMs</StatLabel>
            <StatNumber color="orange.500">{stats.pending}</StatNumber>
          </Stat>
        </Box>
      </SimpleGrid>

      <Stack direction={{ base: "column", md: "row" }} spacing={3} mb={6}>
        <InputGroup maxW={{ base: "full", md: "360px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search request, department, memo number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        <Select
          maxW={{ base: "full", md: "220px" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="generated">Memo Generated</option>
          <option value="pending">Memo Pending</option>
        </Select>
      </Stack>

      {/* Mobile Card View */}
      <VStack spacing={4} display={{ base: "flex", md: "none" }}>
        {filteredRows.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={8}
            textAlign="center"
            w="full"
          >
            <Text color="gray.500">
              No memo records found for current filter.
            </Text>
          </Box>
        ) : (
          filteredRows.map(({ request, memo }) => {
            const requestId = request._id;
            const busy = Boolean(actionLoadingByRequest[requestId]);
            const isExpanded = expandedRequest === requestId;
            return (
              <Card
                key={requestId}
                w="full"
                bg={bgColor}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="flex-start">
                    <Box flex="1">
                      <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>
                        {request.title || "Untitled Request"}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {request.requestNumber || requestId}
                      </Text>
                    </Box>
                    {memo && (
                      <IconButton
                        aria-label="Toggle versions"
                        icon={
                          <Icon
                            as={isExpanded ? BsChevronDown : BsChevronRight}
                          />
                        }
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleVersionHistory(requestId)}
                        ml={2}
                      />
                    )}
                  </Flex>
                </CardHeader>
                <CardBody pt={2}>
                  <VStack spacing={3} align="stretch">
                    <SimpleGrid columns={2} spacing={2} fontSize="sm">
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Department
                        </Text>
                        <Text fontWeight="medium">
                          {request.department || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Status
                        </Text>
                        <Badge colorScheme={memo ? "green" : "orange"}>
                          {memo ? "Generated" : "Pending"}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Memo Number
                        </Text>
                        <Text fontWeight="medium">
                          {memo?.memoNumber || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Version
                        </Text>
                        <Text fontWeight="medium">{memo?.version || "-"}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Generated By
                        </Text>
                        <Text fontWeight="medium" noOfLines={1}>
                          {memo?.createdBy?.name || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Memo Date
                        </Text>
                        <Text fontWeight="medium">
                          {memo?.memoDate
                            ? new Date(memo.memoDate).toLocaleDateString()
                            : "-"}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />

                    <VStack spacing={2} align="stretch">
                      <Button
                        size="sm"
                        leftIcon={<BsFileEarmarkPlus />}
                        colorScheme={memo ? "blue" : "teal"}
                        variant={memo ? "outline" : "solid"}
                        onClick={() => handleGenerateMemo({ request, memo })}
                        isLoading={busy}
                        w="full"
                      >
                        {memo ? "Re-generate" : "Generate"}
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsEye />}
                        variant="outline"
                        isDisabled={!memo?._id || busy}
                        isLoading={previewLoading}
                        onClick={() => handlePreviewMemo(memo._id)}
                        w="full"
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsDownload />}
                        variant="outline"
                        isDisabled={!memo?._id || busy}
                        onClick={() => handleDownloadMemo(memo._id)}
                        w="full"
                      >
                        Download
                      </Button>
                    </VStack>

                    {isExpanded && memo && (
                      <Collapse in={isExpanded} animateOpacity>
                        <Box bg="gray.50" p={3} borderRadius="md" mt={2}>
                          {versionsLoading ? (
                            <Skeleton height="40px" />
                          ) : versionHistory.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">
                              No version history found.
                            </Text>
                          ) : (
                            <VStack spacing={3} align="stretch">
                              <Text fontSize="sm" fontWeight="semibold">
                                Version History
                              </Text>
                              {versionHistory.map((v) => (
                                <Card key={v._id} size="sm" bg="white">
                                  <CardBody>
                                    <VStack spacing={2} align="stretch">
                                      <SimpleGrid
                                        columns={2}
                                        spacing={2}
                                        fontSize="xs"
                                      >
                                        <Box>
                                          <Text color="gray.500">Version</Text>
                                          <Text fontWeight="medium">
                                            v{v.version}
                                          </Text>
                                        </Box>
                                        <Box>
                                          <Text color="gray.500">Date</Text>
                                          <Text fontWeight="medium">
                                            {v.memoDate
                                              ? new Date(
                                                  v.memoDate,
                                                ).toLocaleDateString()
                                              : "-"}
                                          </Text>
                                        </Box>
                                        <Box gridColumn="span 2">
                                          <Text color="gray.500">
                                            Memo Number
                                          </Text>
                                          <Text fontWeight="medium">
                                            {v.memoNumber}
                                          </Text>
                                        </Box>
                                        <Box gridColumn="span 2">
                                          <Text color="gray.500">
                                            Generated By
                                          </Text>
                                          <Text fontWeight="medium">
                                            {v.createdBy?.name || "-"}
                                          </Text>
                                        </Box>
                                      </SimpleGrid>
                                      <HStack spacing={2}>
                                        <Button
                                          size="xs"
                                          leftIcon={<BsEye />}
                                          variant="outline"
                                          onClick={() =>
                                            handlePreviewMemo(v._id)
                                          }
                                          flex="1"
                                        >
                                          Preview
                                        </Button>
                                        <Button
                                          size="xs"
                                          leftIcon={<BsDownload />}
                                          variant="outline"
                                          onClick={() =>
                                            handleDownloadMemo(v._id)
                                          }
                                          flex="1"
                                        >
                                          Download
                                        </Button>
                                      </HStack>
                                    </VStack>
                                  </CardBody>
                                </Card>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })
        )}
      </VStack>

      {/* Desktop Table View */}
      <Box
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
        display={{ base: "none", md: "block" }}
      >
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th w="30px"></Th>
              <Th>Request</Th>
              <Th>Department</Th>
              <Th>Status</Th>
              <Th>Memo Number</Th>
              <Th>Generated By</Th>
              <Th>Memo Date</Th>
              <Th>Version</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={9} py={8} textAlign="center">
                  <Text color="gray.500">
                    No memo records found for current filter.
                  </Text>
                </Td>
              </Tr>
            ) : (
              filteredRows.map(({ request, memo }) => {
                const requestId = request._id;
                const busy = Boolean(actionLoadingByRequest[requestId]);
                const isExpanded = expandedRequest === requestId;
                return (
                  <React.Fragment key={requestId}>
                    <Tr>
                      <Td p={1}>
                        {memo && (
                          <IconButton
                            aria-label="Toggle versions"
                            icon={
                              <Icon
                                as={isExpanded ? BsChevronDown : BsChevronRight}
                              />
                            }
                            size="xs"
                            variant="ghost"
                            onClick={() => toggleVersionHistory(requestId)}
                          />
                        )}
                      </Td>
                      <Td>
                        <Text fontWeight="semibold" noOfLines={1} maxW="280px">
                          {request.title || "Untitled Request"}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {request.requestNumber || requestId}
                        </Text>
                      </Td>
                      <Td>{request.department || "-"}</Td>
                      <Td>
                        <Badge colorScheme={memo ? "green" : "orange"}>
                          {memo ? "Generated" : "Pending"}
                        </Badge>
                      </Td>
                      <Td>{memo?.memoNumber || "-"}</Td>
                      <Td>{memo?.createdBy?.name || "-"}</Td>
                      <Td>
                        {memo?.memoDate
                          ? new Date(memo.memoDate).toLocaleDateString()
                          : "-"}
                      </Td>
                      <Td>{memo?.version || "-"}</Td>
                      <Td>
                        <VStack spacing={2} align="stretch">
                          <Button
                            size="xs"
                            minW={{ base: "90px", md: "110px" }}
                            leftIcon={<BsFileEarmarkPlus />}
                            colorScheme={memo ? "blue" : "teal"}
                            variant={memo ? "outline" : "solid"}
                            onClick={() =>
                              handleGenerateMemo({ request, memo })
                            }
                            isLoading={busy}
                          >
                            {memo ? "Re-generate" : "Generate"}
                          </Button>
                          <Button
                            size="xs"
                            minW={{ base: "90px", md: "110px" }}
                            leftIcon={<BsEye />}
                            variant="outline"
                            isDisabled={!memo?._id || busy}
                            isLoading={previewLoading}
                            onClick={() => handlePreviewMemo(memo._id)}
                          >
                            Preview
                          </Button>
                          <Button
                            size="xs"
                            minW={{ base: "90px", md: "110px" }}
                            leftIcon={<BsDownload />}
                            variant="outline"
                            isDisabled={!memo?._id || busy}
                            onClick={() => handleDownloadMemo(memo._id)}
                          >
                            Download
                          </Button>
                        </VStack>
                      </Td>
                    </Tr>
                    {isExpanded && memo && (
                      <Tr>
                        <Td colSpan={9} p={0} borderBottom="none">
                          <Collapse in={isExpanded} animateOpacity>
                            <Box bg="gray.50" px={6} py={3}>
                              {versionsLoading ? (
                                <Skeleton height="40px" />
                              ) : versionHistory.length === 0 ? (
                                <Text fontSize="sm" color="gray.500">
                                  No version history found.
                                </Text>
                              ) : (
                                <Table size="xs" variant="simple">
                                  <Thead>
                                    <Tr>
                                      <Th fontSize="xs">Version</Th>
                                      <Th fontSize="xs">Memo Number</Th>
                                      <Th fontSize="xs">Generated By</Th>
                                      <Th fontSize="xs">Date</Th>
                                      <Th fontSize="xs">Actions</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {versionHistory.map((v) => (
                                      <Tr key={v._id}>
                                        <Td fontSize="sm">v{v.version}</Td>
                                        <Td fontSize="sm">{v.memoNumber}</Td>
                                        <Td fontSize="sm">
                                          {v.createdBy?.name || "-"}
                                        </Td>
                                        <Td fontSize="sm">
                                          {v.memoDate
                                            ? new Date(
                                                v.memoDate,
                                              ).toLocaleDateString()
                                            : "-"}
                                        </Td>
                                        <Td>
                                          <VStack spacing={2} align="stretch">
                                            <Button
                                              size="xs"
                                              leftIcon={<BsEye />}
                                              variant="outline"
                                              onClick={() =>
                                                handlePreviewMemo(v._id)
                                              }
                                            >
                                              Preview
                                            </Button>
                                            <Button
                                              size="xs"
                                              leftIcon={<BsDownload />}
                                              variant="outline"
                                              onClick={() =>
                                                handleDownloadMemo(v._id)
                                              }
                                            >
                                              Download
                                            </Button>
                                          </VStack>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default InterOfficeMemo;
